<template>
  <div class="tab-descriptor">
    <div class="descriptor-content">
      <div class="descriptor-header">
        <i :class="icon" class="descriptor-icon"></i>
        <h3 class="descriptor-title">{{ title }}</h3>
        <Button
          v-if="tooltip"
          icon="pi pi-info-circle"
          severity="secondary"
          text
          rounded
          size="small"
          v-tooltip.bottom="tooltip"
          class="info-button"
          data-testid="tab-descriptor-info"
        />
      </div>
      <p class="descriptor-description">{{ description }}</p>

      <!-- Progressive Disclosure: Accordion Sections -->
      <Accordion v-if="guidanceSections && guidanceSections.length > 0" class="guidance-accordion">
        <AccordionPanel v-for="(section, index) in guidanceSections" :key="index" :value="index">
          <AccordionHeader>
            <i :class="section.icon || 'pi pi-info-circle'" class="section-icon"></i>
            {{ section.title }}
          </AccordionHeader>
          <AccordionContent>
            <div class="section-content">
              <p v-if="section.description" class="section-description">{{ section.description }}</p>
              <div v-if="section.tips && section.tips.length > 0" class="section-tips">
                <div v-for="(tip, tipIndex) in section.tips" :key="tipIndex"
                     class="tip-item"
                     :class="{ 'tip-example': isExample(tip) }">
                  <i v-if="!isExample(tip)" class="pi pi-check tip-icon"></i>
                  <span class="tip-text" v-html="formatTipSanitizado(tip)"></span>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>

      <!-- Legacy: Simple tips (backward compatibility) -->
      <div v-else-if="tips && tips.length > 0" class="descriptor-tips">
        <div v-for="(tip, index) in tips" :key="index" class="tip-item" :class="{ 'tip-header': isHeaderTip(tip), 'tip-example': isExample(tip) }">
          <i v-if="!isHeaderTip(tip)" class="pi pi-lightbulb tip-icon"></i>
          <span class="tip-text" v-html="formatTipSanitizado(tip)"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { sanitizeHtmlContent } from '../utils/sanitizeHtmlContent';

interface GuidanceSection {
  title: string;
  icon?: string;
  description?: string;
  tips: string[];
}

defineProps<{
  icon: string;
  title: string;
  description: string;
  tooltip?: string | null;
  guidanceSections?: GuidanceSection[];
  tips?: string[];
}>();

// Helper to detect header-style tips (empty or contains only special chars)
const isHeaderTip = (tip: string): boolean => {
  return !tip || tip.trim() === '' || tip.endsWith(':');
};

// Helper to detect example blocks (contain box drawing characters or math symbols)
const isExample = (tip: string): boolean => {
  return Boolean(tip && (tip.includes('┌') || tip.includes('R$') || tip.includes('──') || tip.includes('│')));
};

// Helper to format tips with special styling for examples
/**
 * Monta o HTML da dica — e sanitiza ANTES de devolver.
 *
 * Hoje todos os chamadores passam array LITERAL de código (verificado: nenhum
 * `:tips=` recebe dado de API), então não há injeção agora. Mas a função
 * INTERPOLA `tip` dentro de HTML sem escapar, e nada impede alguém de ligar
 * essas dicas a um texto vindo do banco amanhã — o defeito ficaria pronto,
 * esperando. Sanitizar aqui custa uma chamada e fecha o caminho.
 */
const formatTipSanitizado = (tip: string): string => {
  if (!tip) return '';

  // Detect box drawing characters for examples
  if (tip.includes('┌') || tip.includes('│') || tip.includes('└')) {
    return sanitizeHtmlContent(`<span class="tip-box">${tip}</span>`);
  }

  // Bold the header part (before the colon in headers)
  if (tip.includes(':') && !tip.includes('•')) {
    const parts = tip.split(':');
    if (parts.length >= 2) {
      return sanitizeHtmlContent(`<strong>${parts[0]}:</strong>${parts.slice(1).join(':')}`);
    }
  }

  return sanitizeHtmlContent(tip);
};
</script>

<style scoped>
.tab-descriptor {
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.descriptor-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.descriptor-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.descriptor-icon {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.descriptor-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  flex: 1;
}

.info-button {
  flex-shrink: 0;
}

.descriptor-description {
  margin: 0;
  color: var(--text-color-secondary);
  line-height: 1.6;
  font-size: 0.95rem;
}

.descriptor-tips {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.tip-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--surface-50);
  border-radius: 6px;
  border-left: 3px solid var(--primary-color);
}

.tip-item:has(.tip-text:empty) {
  display: none;
}

.tip-item.tip-header {
  background: transparent;
  border-left: none;
  padding: 0.75rem 0.75rem 0.25rem 0.75rem;
  margin-top: 0.5rem;
}

.tip-item.tip-header:first-of-type {
  margin-top: 0;
  padding-top: 0;
}

.tip-item.tip-header .tip-text {
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.95rem;
}

.tip-item.tip-example {
  background: var(--blue-50);
  border-left-color: var(--blue-500);
  padding: 0.75rem;
}

.tip-item.tip-example .tip-text {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.85rem;
  line-height: 1.4;
  letter-spacing: 0.05em;
}

.tip-item.tip-example :deep(.tip-box) {
  color: var(--blue-700);
  font-weight: 500;
}

.tip-icon {
  color: var(--yellow-500);
  font-size: 1rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.tip-text {
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-line;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Accordion Styles */
.guidance-accordion {
  margin-top: 1rem;
}

.guidance-accordion :deep(.p-accordionheader) {
  border-radius: 6px;
  padding: 0.75rem 1rem;
  background: var(--surface-100);
  border: 1px solid var(--surface-border);
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-color);
  transition: all 0.2s;
}

.guidance-accordion :deep(.p-accordionheader:hover) {
  background: var(--surface-200);
}

.guidance-accordion :deep(.p-accordionheader .p-accordionheader-link) {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-icon {
  color: var(--primary-color);
  font-size: 1rem;
}

.section-content {
  padding: 0.75rem 0;
}

.section-description {
  margin: 0 0 0.75rem 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
}

.section-tips {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-tips .tip-item {
  padding: 0.5rem 0.75rem;
  background: var(--surface-50);
  border-radius: 4px;
  border-left: 3px solid var(--primary-color);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.section-tips .tip-item .tip-icon {
  color: var(--green-500);
  font-size: 0.9rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.section-tips .tip-example {
  background: var(--blue-50);
  border-left-color: var(--blue-500);
  padding: 0.75rem;
}

.section-tips .tip-example .tip-text {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.85rem;
  line-height: 1.4;
  letter-spacing: 0.05em;
  color: var(--blue-700);
  font-weight: 500;
}
</style>
